import { Box, useColorModeValue } from '@chakra-ui/react';
import { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  BarChart,
  StackedBarChart,
  HorizontalBarChart,
  StackedHorizontalBarChart,
  LineChart,
  DoughnutChart,
  BaseChartProps,
} from './charts';
import { ChakraColor } from 'src/utils/color-util';
import { AggregatedOrderViewType } from 'src/gql/graphql';

// Register all required Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface AggregatedOrderChartProps extends BaseChartProps {
  viewType?: AggregatedOrderViewType;
}

export default function AggregatedOrderChart({
  table,
  grouping,
  rowSelection,
  setRowSelection,
  viewType = AggregatedOrderViewType.StackedBarChart,
}: AggregatedOrderChartProps) {
  const borderColor = useColorModeValue(
    ChakraColor.gray200,
    ChakraColor.gray700
  );
  const textColor = useColorModeValue(ChakraColor.gray800, ChakraColor.gray200);

  // Get current table states to use as dependencies
  const tableStateGrouping = table.getState().grouping;
  const tableStateColumnFilters = table.getState().columnFilters;

  // Calculate dataLength based on current rows, reactive to table state changes
  const dataLength = useMemo(() => {
    return table.getRowModel().rows.length;
  }, [table, tableStateGrouping, tableStateColumnFilters]);

  // Calculate dynamic height based on data length for better vertical scrolling
  const dynamicHeight = useMemo(() => {
    // Base height of 300px, with additional height for each data point
    // Adjust the multiplier (30) based on your chart's needs
    return Math.max(300, dataLength * 30);
  }, [dataLength]);

  // Calculate dynamic width based on number of data points for horizontal scrolling
  const dynamicWidth = useMemo(() => {
    // Base width of 600px, with additional width for each data point
    // Only for charts that benefit from horizontal scaling
    if (
      viewType === AggregatedOrderViewType.BarChart ||
      viewType === AggregatedOrderViewType.StackedBarChart ||
      viewType === AggregatedOrderViewType.LineChart
    ) {
      // Minimum width per data point to ensure readability
      const minWidthPerDataPoint = 60; // Adjust this value as needed
      return Math.max(600, dataLength * minWidthPerDataPoint);
    }
    return 600; // Default minimum width for other chart types
  }, [dataLength, viewType]);

  const chartProps = useMemo(
    () => ({
      table,
      grouping,
      rowSelection,
      setRowSelection,
      borderColor,
      textColor,
    }),
    [table, grouping, rowSelection, setRowSelection, borderColor, textColor]
  );

  const renderChart = () => {
    switch (viewType) {
      case AggregatedOrderViewType.BarChart:
        return <BarChart {...chartProps} />;
      case AggregatedOrderViewType.StackedBarChart:
        return <StackedBarChart {...chartProps} />;
      case AggregatedOrderViewType.HorizontalBarChart:
        return <HorizontalBarChart {...chartProps} />;
      case AggregatedOrderViewType.StackedHorizontalBarChart:
        return <StackedHorizontalBarChart {...chartProps} />;
      case AggregatedOrderViewType.LineChart:
        return <LineChart {...chartProps} />;
      case AggregatedOrderViewType.DoughnutChart:
        return <DoughnutChart {...chartProps} />;
      default:
        return <StackedBarChart {...chartProps} />;
    }
  };

  return (
    <Box
      padding={4}
      minWidth="600px"
      overflowY="auto"
      overflowX="auto"
      position="relative"
    >
      {viewType === AggregatedOrderViewType.DoughnutChart ||
      viewType === AggregatedOrderViewType.BarChart ||
      viewType === AggregatedOrderViewType.StackedBarChart ||
      viewType === AggregatedOrderViewType.LineChart ? (
        // These charts should fit within the parent container height
        <Box
          height="100%"
          minHeight="200px"
          width={`${dynamicWidth}px`}
          minWidth="600px"
        >
          {renderChart()}
        </Box>
      ) : (
        // Other charts use dynamic height for scrolling
        <Box
          height={`${dynamicHeight}px`}
          width={`${dynamicWidth}px`}
          minWidth="600px"
        >
          {renderChart()}
        </Box>
      )}
    </Box>
  );
}
